<!--
  Share a document, and see who has it. TASK-038 phase 2.

  The user's spec, and what each sentence of it decides here:

    "It accepts email addresses, including ones already shared with -- re-adding
     is an UPSERT that CHANGES their permission, which is the point: it is how a
     person fixes several people at once."

  So the field never refuses an address for being on the ACCESS list already,
  and the access list below it is drawn from the SERVER's answer after every
  change rather than patched locally: the one thing this dialog must never do
  is show a role the database does not hold. (A duplicate PILL is a different
  thing and is a no-op -- see the address field below.)

    "One recipient per call -- if the dialog accepts several addresses, issue one
     request each with visible progress, so the number a person sees is the
     number that happens."

  So `POST /v1/docs/:id/shares` is called once per address, in order, and each
  line reports its own outcome. One failure does not cancel the rest, because
  four grants that worked are four grants that worked -- and it does not roll
  the others back either, because a permission change is not a transaction a
  person expects to be undone by somebody else's typo.

  THREE THINGS THAT ARE NOT DECORATION.

  1. `sessionOnly: true` on every call. These four routes refuse ANY request
     carrying an `Authorization` header -- a leaked agent token must not be able
     to write a grant that outlives its own revocation -- and `api()` otherwise
     attaches a developer bearer whenever localStorage holds one. Without this
     flag the dialog 403s on a developer's machine and works everywhere else,
     which is the worst way to find out.

  2. The sentence about agents (§8). Sharing with a person shares with every
     agent they have connected, at the same role, immediately. It is the largest
     consequence of the feature, so it is on the screen in words, and the access
     list carries the live-token count per person beside it.

  3. `may_share` is offered but off by default. TASK-040 is what makes a
     recipient able to USE it; writing it now means that ticket inherits a
     populated column rather than a backfill of rows whose grantor is no longer
     around to be asked.

  TWO CONTROLS THE USER ASKED FOR BY HAND, AFTER USING IT.

  A. THE ADDRESS FIELD IS PILLS, not a textarea.

       "When I press space, comma, semicolon, or whatever after the email
        address, it needs to validate a valid email address, and then that needs
        to become a pill with a little cross on it to remove."

     The logic is in `doclist-types.ts` (`commitAddresses` and friends) because
     it is the part that can be unit-tested; this file is the gestures and the
     focus. What the markup is careful about:

       * A `<ul>` of pills and a labelled `<input type="email">`. NOT a
         listbox, not a combobox, not a grid: the pills are a list of things
         that are already chosen, and dressing them as a widget would make a
         screen reader promise arrow keys that do not exist.
       * Each cross is a real `<button>` named after its address --
         `aria-label="Remove ana@example.com"` -- because a button whose whole
         accessible name is "×" announces as nothing a person can act on.
       * Focus after a removal is DECIDED (`focusAfterRemove`): the next pill's
         cross, or the input when there is no next pill. The button that was
         focused has just been deleted, and focus left on `<body>` inside a
         modal is how a keyboard user gets stranded.

  B. THE PERMISSION CONTROL IS A LISTBOX, not a native `<select>`.

       "This needs to be a better-looking HTML dropdown. The native dropdown
        doesn't do it for me. It looks terrible."

     A `<select>` draws its popup in OS chrome -- a dark panel with a blue
     selection bar on macOS -- which no stylesheet can reach. So: a button with
     `aria-haspopup="listbox"` and a `<ul role="listbox">` in the app's own
     palette, with the keyboard the pattern owes (`listbox.ts`).

     ONE focus model, not two: DOM focus goes to the LIST and
     `aria-activedescendant` names the active option. The options are never
     focused and carry no tabindex. Mixing roving focus with activedescendant
     is the usual bug in a hand-built listbox.

     The panel is `position: fixed` and placed from the button's rect. Inside a
     sheet that is `overflow-y: auto`, an absolutely positioned panel is
     clipped by the scroll box the moment the list is near the bottom; a fixed
     one is laid out against the viewport instead, and its `z-index` still
     resolves inside the backdrop's stacking context, so it sits over the
     sheet's own content.

  ONE live region in this form, not three. An earlier review found the list
  pane announcing everything twice because a live container wrapped two more,
  so the progress list is NOT `aria-live`: every announcement this form makes
  -- a pill added, a pill removed, a duplicate ignored, a rejection, and the
  outcome of a send -- goes through the single `role="status"` line.
-->
<script lang="ts">
  import { tick } from 'svelte'
  import { api, describeError } from '../api'
  import CopyBox from './CopyBox.svelte'
  import { closedKey, openKey, typeAhead } from './listbox'
  import {
    ROLE_BLURB,
    SHAREABLE_ROLES,
    addressNews,
    backspaceRemoves,
    commitAddresses,
    commitsAddress,
    focusAfterRemove,
    type AccessEntry,
    type AccessList,
    type DocRow,
    type Role,
    type ShareAttempt,
  } from './doclist-types'

  let {
    doc,
    onclose,
    onchanged,
  }: {
    doc: DocRow
    onclose: () => void
    /** The list behind the dialog may want to know something moved. */
    onchanged?: () => void
  } = $props()

  /** The committed addresses, in order. One pill each. */
  let tokens = $state<string[]>([])
  /** What is in the input and not yet a pill. */
  let draft = $state('')
  /** The one short line under the field, and what `aria-describedby` points at. */
  let fieldError = $state('')
  /** The only thing this form announces. See the note above. */
  let news = $state('')

  let role = $state<Role>('viewer')
  let mayShare = $state(false)

  let entries = $state<AccessEntry[]>([])
  let loading = $state(true)
  let listError = $state('')
  let formError = $state('')
  let sending = $state(false)
  let attempts = $state<ShareAttempt[]>([])
  /** Which row is mid-revoke, so only that one's button goes quiet. */
  let revoking = $state<string | null>(null)

  let inputEl = $state<HTMLInputElement | null>(null)
  let pillsEl = $state<HTMLUListElement | null>(null)

  const canSend = $derived((tokens.length > 0 || draft.trim().length > 0) && !sending)

  /* --------------------------------------------------- published to the web
   *
   * A different kind of act from sharing, and the dialog says so rather than
   * looking like one more row in the list: sharing hands the document to
   * somebody you named, this hands it to everyone the link ever reaches.
   * Owner only -- the endpoint enforces that, and the control is not drawn for
   * anyone else so nobody presses a button that is going to refuse them.
   */
  let isPublic = $state(false)
  let publicAt = $state<string | null>(null)
  let publishing = $state(false)
  let publishError = $state('')
  /*
   * Whether to offer this at all comes from the access response, NOT from a
   * second lookup of who I am.
   *
   * It was `myUserId()` compared against the owner row, and that was a bug
   * with a nasty shape: a second async source meant the section could appear
   * AFTER the dialog was already on screen. The sheet is vertically centred
   * (`.backdrop` is `place-items: center`), so anything appearing late lifts
   * every control in the dialog -- and a click between mousedown and mouseup
   * lands on nothing. It broke share.spec.ts H21, where a click on Share
   * committed the typed address and then shared nobody.
   *
   * One response, one layout change, and it happens as the list itself
   * appears.
   */
  let canPublish = $state(false)

  /** The document's address, which is the same one it always had. */
  const publicUrl = $derived(`${location.origin}/d/${doc.id}`)

  async function togglePublic(next: boolean) {
    publishing = true
    publishError = ''
    try {
      const out = await api<{ public: boolean; publicAt: string | null }>(
        `/v1/docs/${doc.id}/public`,
        { method: 'PUT', body: JSON.stringify({ public: next }), sessionOnly: true },
      )
      isPublic = out.public
      publicAt = out.publicAt
    } catch (err) {
      publishError = `Could not ${next ? 'publish' : 'unpublish'} this document: ${describeError(err)}.`
    } finally {
      publishing = false
    }
  }

  async function loadAccess() {
    loading = true
    listError = ''
    try {
      const out = await api<AccessList>(`/v1/docs/${doc.id}/access`, { sessionOnly: true })
      entries = out.entries ?? []
      isPublic = out.public === true
      publicAt = out.publicAt ?? null
      canPublish = out.canPublish === true
    } catch (err) {
      listError = `Could not load who has access: ${describeError(err)}.`
    } finally {
      loading = false
    }
  }

  $effect(() => {
    void loadAccess()
  })

  /* ------------------------------------------------------ the address field */

  /**
   * Turn text into pills.
   *
   * Everything that passes becomes a pill; everything that does not stays in
   * the input with a line saying why. The caller never has to decide which is
   * which -- `commitAddresses` returns both, and the same call serves a typed
   * delimiter, a paste of nine addresses and a blur.
   */
  function commitDraft(raw: string) {
    const result = commitAddresses(tokens, raw)
    tokens = result.tokens
    draft = result.rest
    fieldError = result.error
    const said = addressNews(result)
    if (said) news = said
  }

  async function removeAddress(index: number) {
    const gone = tokens[index]
    if (gone === undefined) return
    const plan = focusAfterRemove(tokens.length, index)
    tokens = tokens.filter((_, n) => n !== index)
    news = `Removed ${gone}`
    // The focused button is about to stop existing, so the move has to happen
    // after the list has redrawn -- otherwise it lands on a node Svelte is
    // replacing and focus falls to <body>.
    await tick()
    if (plan.target === 'pill') {
      const crosses = pillsEl?.querySelectorAll<HTMLButtonElement>('.pill-x')
      const next = crosses?.[plan.index]
      if (next) {
        next.focus()
        return
      }
    }
    inputEl?.focus()
  }

  function onFieldKey(event: KeyboardEvent) {
    if (event.key === 'Backspace') {
      const doomed = backspaceRemoves(draft, tokens.length)
      if (doomed !== null) {
        event.preventDefault()
        void removeAddress(doomed)
      }
      return
    }
    if (!commitsAddress(event.key)) return
    if (!draft.trim()) {
      // Nothing to commit. Enter submits the form and Tab moves on, both of
      // which are what a person pressing them with an empty field wants; a
      // stray delimiter is swallowed so the field does not fill with commas.
      if (event.key !== 'Enter' && event.key !== 'Tab') event.preventDefault()
      return
    }
    // Tab commits but is never prevented: swallowing it would trap the
    // keyboard in this field, and the blur it causes would commit anyway.
    if (event.key !== 'Tab') event.preventDefault()
    commitDraft(draft)
  }

  /**
   * Paste of several addresses becomes several pills in one go.
   *
   * A paste with no delimiter in it is left alone -- it is somebody pasting
   * one address, or half of one, and committing it on arrival would reject
   * `ana@` before they had finished typing the domain.
   */
  function onFieldPaste(event: ClipboardEvent) {
    const text = event.clipboardData?.getData('text') ?? ''
    if (!text.trim() || !/[\s,;]/.test(text.trim())) return
    event.preventDefault()
    // Joined with a space rather than concatenated: whatever was already in
    // the input is its own address, not a prefix of the first pasted one.
    commitDraft(`${draft} ${text}`)
  }

  /**
   * Blur commits what is in the input.
   *
   * Somebody who types one address and clicks Share must not lose it, and this
   * is the single most common way a token field frustrates people. Blur runs
   * before the click that caused it, so the pill exists by the time `send`
   * reads the list.
   */
  /**
   * Turn what is typed into a pill when the field loses focus -- UNLESS focus
   * is on its way to a control in this same dialog.
   *
   * Committing inserts a pill row, which makes the form one line taller. The
   * sheet is `max-height: 85vh` with `overflow-y: auto`, so once it is tall
   * enough to be capped that extra line does not grow the dialog: it pushes
   * everything below it DOWN by a whole row, inside the scroller. Do that
   * during the blur that a click on Share caused and the button travels out
   * from under the pointer between mousedown and mouseup, and the click never
   * happens. The address gets its pill and nothing is shared -- which is
   * exactly what share.spec.ts H21 caught, the day the dialog grew enough to
   * reach its cap.
   *
   * Nothing is lost by waiting: `send()` already takes whatever is still in
   * the input ("a person who typed an address and pressed Share has not 'not
   * finished typing'"), so the pill appears a moment later, from the submit,
   * with the click safely spent.
   *
   * Focus leaving the dialog entirely still commits, which is the case this
   * handler was written for.
   *
   * Same shape as BUG-022: do not move what the pointer is on, mid-gesture.
   */
  function onFieldBlur(event: FocusEvent) {
    const to = event.relatedTarget
    if (to instanceof Node && formEl?.contains(to)) return
    if (draft.trim()) commitDraft(draft)
  }

  /* ---------------------------------------------------- the permission list */

  let roleOpen = $state(false)
  let roleActive = $state(0)
  let panel = $state({ left: 0, top: 0, width: 0 })
  let formEl = $state<HTMLFormElement | null>(null)
  let roleBtnEl = $state<HTMLButtonElement | null>(null)
  let roleListEl = $state<HTMLUListElement | null>(null)

  const roleIndex = $derived(Math.max(0, SHAREABLE_ROLES.indexOf(role)))

  /**
   * Place the panel against the button, in viewport coordinates.
   *
   * Below it by preference, above it when that would run off the bottom, and
   * never off the right edge. Measured from the rendered panel, so it is
   * called once it exists and again on every scroll and resize.
   */
  function place() {
    const box = roleBtnEl?.getBoundingClientRect()
    if (!box) return
    const edge = 8
    const gap = 4
    const height = roleListEl?.offsetHeight ?? 0
    const width = roleListEl?.offsetWidth ?? box.width

    let top = box.bottom + gap
    if (height && top + height > window.innerHeight - edge) {
      const above = box.top - gap - height
      top = above >= edge ? above : Math.max(edge, window.innerHeight - edge - height)
    }

    let left = box.left
    if (left + width > window.innerWidth - edge) {
      left = Math.max(edge, window.innerWidth - edge - width)
    }

    panel = { left, top, width: box.width }
  }

  async function openRole(active = roleIndex) {
    roleActive = active
    roleOpen = true
    // Measured after it exists: the flip-above decision needs its height.
    await tick()
    place()
    roleListEl?.focus()
  }

  function closeRole(refocus = true) {
    roleOpen = false
    if (refocus) roleBtnEl?.focus()
  }

  function pickRole(index: number) {
    const next = SHAREABLE_ROLES[index]
    if (next) role = next
    closeRole()
  }

  function onRoleButtonKey(event: KeyboardEvent) {
    const { action, prevent } = closedKey(event.key, roleIndex, SHAREABLE_ROLES.length)
    if (action.kind !== 'open') return
    if (prevent) event.preventDefault()
    void openRole(action.active)
  }

  /**
   * Clicks are taken on the LIST, not on each option.
   *
   * One handler beside the keydown the list already carries, rather than a
   * click on every `<li>`: an option is not a button, and hanging a bare click
   * on a list item is the shape a11y tooling rightly complains about.
   */
  function onRoleListClick(event: MouseEvent) {
    const hit = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-index]')
    if (!hit) return
    pickRole(Number(hit.dataset.index))
  }

  function onRoleListKey(event: KeyboardEvent) {
    const { action, prevent } = openKey(event.key, roleActive, SHAREABLE_ROLES.length)
    if (action.kind === 'none') {
      const hit = typeAhead(SHAREABLE_ROLES, event.key, roleActive)
      if (hit >= 0) {
        event.preventDefault()
        roleActive = hit
      }
      return
    }
    if (prevent) event.preventDefault()
    // Escape closes the LIST. Without this it would also reach whatever else
    // is listening for it and close the dialog underneath.
    if (event.key === 'Escape') event.stopPropagation()
    if (action.kind === 'move') roleActive = action.active
    else if (action.kind === 'choose') pickRole(action.index)
    else if (action.kind === 'close') closeRole(event.key !== 'Tab')
  }

  $effect(() => {
    if (!roleOpen) return
    const reposition = () => place()
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (target && (roleListEl?.contains(target) || roleBtnEl?.contains(target))) return
      const hadFocus = !!roleListEl?.contains(document.activeElement)
      // Closed WITHOUT pulling focus back to the button, because the pointer
      // is on its way to something else in this dialog and yanking focus out
      // of what a person just clicked is worse than the inconsistency.
      roleOpen = false
      if (!hadFocus) return
      // But the list that HELD focus has just been removed. Whatever was
      // clicked takes focus first if it can; if nothing did, focus would be
      // left on <body> inside a modal, so the button takes it back.
      setTimeout(() => {
        if (document.activeElement === document.body) roleBtnEl?.focus()
      }, 0)
    }
    // Capture, so a scroll inside the sheet repositions it too.
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    window.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('pointerdown', onPointerDown, true)
    }
  })

  /* ------------------------------------------------------------- the send */

  /**
   * One request per address, in order, each reporting its own outcome.
   *
   * Sequential rather than `Promise.all`: the per-user send bucket is real (a
   * share posts mail from the deployment), and five parallel requests would
   * meet it as a burst and fail addresses that would have gone through. It also
   * keeps the progress readable, which is the whole reason the loop is visible.
   */
  async function send() {
    formError = ''
    // Whatever is still in the input goes too. A person who typed an address
    // and pressed Enter on the Share button has not "not finished typing".
    if (draft.trim()) commitDraft(draft)
    const list = tokens
    if (!list.length) {
      // `fieldError` has already said what is wrong when the draft was junk;
      // saying it twice in two places is how a form starts shouting.
      if (!fieldError) formError = 'Add an email address first.'
      return
    }
    // Every address here passed `looksLikeEmail` on its way to becoming a
    // pill, so there is no second validation pass. The server is what actually
    // decides, and its sentence wins whenever it disagrees.

    sending = true
    news = `Sharing with ${list.length} ${list.length === 1 ? 'person' : 'people'}…`
    attempts = list.map((email) => ({ email, state: 'waiting' as const }))
    for (const [i, email] of list.entries()) {
      attempts = attempts.map((a, n) => (n === i ? { ...a, state: 'sending' } : a))
      try {
        const out = await api<{ mailed: boolean; pending?: boolean }>(`/v1/docs/${doc.id}/shares`, {
          method: 'POST',
          sessionOnly: true,
          body: JSON.stringify({ email, role, mayShare }),
        })
        attempts = attempts.map((a, n) =>
          n === i ? { ...a, state: 'done', mailed: out.mailed, pending: out.pending === true } : a,
        )
      } catch (err) {
        // One failure does not stop the rest: four grants that worked are four
        // grants that worked, and the person can see which one did not.
        attempts = attempts.map((a, n) =>
          n === i ? { ...a, state: 'failed', error: describeError(err) } : a,
        )
      }
    }
    sending = false

    // Only the addresses that did NOT land are left as pills, so pressing
    // Share again retries exactly those and nothing else.
    const failed = attempts.filter((a) => a.state === 'failed').map((a) => a.email)
    tokens = failed
    /*
     * A recipient with no account is a DIFFERENT outcome and is said
     * differently. "Shared with 1 person" is what happened to a colleague;
     * somebody who has never signed in has had an account made for them and
     * the document is waiting rather than being read. Telling the owner it
     * went to a colleague would be telling them the wrong thing.
     */
    const invited = attempts.filter((a) => a.state === 'done' && a.pending).length
    const landed = list.length - failed.length
    news = failed.length
      ? `Shared with ${landed} of ${list.length}. ${failed.length} did not go through.`
      : `Shared with ${landed} ${landed === 1 ? 'person' : 'people'}.`
    if (invited > 0) {
      news +=
        invited === 1
          ? ' One of them does not have an account yet — we have emailed them, and the document will be waiting the first time they sign in.'
          : ` ${invited} of them do not have accounts yet — we have emailed them, and the document will be waiting the first time they sign in.`
    }

    await loadAccess()
    onchanged?.()
  }

  async function revoke(entry: AccessEntry) {
    if (!entry.userId) return
    revoking = entry.userId
    listError = ''
    try {
      await api(`/v1/docs/${doc.id}/shares/${entry.userId}`, {
        method: 'DELETE',
        sessionOnly: true,
      })
      await loadAccess()
      onchanged?.()
    } catch (err) {
      listError = `That did not work: ${describeError(err)}.`
    } finally {
      revoking = null
    }
  }

  /** What the list says about HOW somebody got here. */
  function viaWords(entry: AccessEntry): string {
    if (entry.via === 'owner') return 'Owner'
    if (entry.via === 'org') return 'Everyone in this organisation'
    if (entry.via === 'link') return 'Anyone with the link'
    if (entry.via === 'folder') return 'Through a folder'
    return entry.grantedBy ? `Shared by ${entry.grantedBy.name}` : 'Shared directly'
  }

  function agentWords(n: number): string {
    if (!n) return ''
    return `${n} connected agent${n === 1 ? '' : 's'}`
  }
</script>

<div class="backdrop">
  <button type="button" class="backdrop-hit" aria-label="Close" onclick={onclose}></button>
  <div class="sheet" role="dialog" aria-modal="true" aria-label={`Share "${doc.name}"`}>
    <h2 class="sheet-title">Share “{doc.name}”</h2>

    <!-- `novalidate`: the input keeps `type="email"` for the semantics and the
         phone keyboard, but the browser's own bubble must not fire on the
         half-typed address this field deliberately leaves in the box -- the
         line under the field is what says what is wrong, in our own words. -->
    <form
      bind:this={formEl}
      class="share-form"
      novalidate
      onsubmit={(e) => {
        e.preventDefault()
        if (canSend) void send()
      }}
    >
      <div class="field">
        <label class="field-label" for="share-to">Email addresses</label>
        <div class="tokens" class:tokens-bad={!!fieldError}>
          {#if tokens.length}
            <ul class="pills" bind:this={pillsEl}>
              {#each tokens as address, i (address.toLowerCase())}
                <li class="pill">
                  <span class="pill-text">{address}</span>
                  <button
                    type="button"
                    class="pill-x"
                    aria-label={`Remove ${address}`}
                    onclick={() => removeAddress(i)}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </li>
              {/each}
            </ul>
          {/if}
          <!-- svelte-ignore a11y_autofocus -->
          <input
            id="share-to"
            class="token-input"
            type="email"
            autocomplete="off"
            autofocus
            placeholder={tokens.length ? 'Add another' : 'ana@example.com'}
            aria-invalid={fieldError ? 'true' : undefined}
            aria-describedby={fieldError ? 'share-to-error' : undefined}
            bind:value={draft}
            bind:this={inputEl}
            onkeydown={onFieldKey}
            onpaste={onFieldPaste}
            onblur={onFieldBlur}
          />
        </div>
        {#if fieldError}
          <p class="field-bad" id="share-to-error">{fieldError}</p>
        {:else}
          <p class="field-note">Space, comma or Enter turns an address into a pill.</p>
        {/if}
      </div>

      <div class="row">
        <div class="field field-inline">
          <span class="field-label" id="share-role-label">Permission</span>
          <button
            type="button"
            id="share-role"
            class="field-input picker"
            aria-haspopup="listbox"
            aria-expanded={roleOpen}
            aria-controls={roleOpen ? 'share-role-list' : undefined}
            aria-labelledby="share-role-label share-role"
            bind:this={roleBtnEl}
            onclick={() => (roleOpen ? closeRole() : void openRole())}
            onkeydown={onRoleButtonKey}
          >
            <span class="picker-name">{role}</span>
            <span class="picker-blurb">— {ROLE_BLURB[role]}</span>
            <span class="picker-caret" aria-hidden="true">▾</span>
          </button>
          {#if roleOpen}
            <ul
              id="share-role-list"
              class="picker-list"
              role="listbox"
              tabindex="-1"
              aria-labelledby="share-role-label"
              aria-activedescendant={`share-role-opt-${roleActive}`}
              style={`left:${panel.left}px; top:${panel.top}px; min-width:${panel.width}px`}
              bind:this={roleListEl}
              onkeydown={onRoleListKey}
              onclick={onRoleListClick}
            >
              {#each SHAREABLE_ROLES as r, i (r)}
                <li
                  id={`share-role-opt-${i}`}
                  class="picker-option"
                  class:active={i === roleActive}
                  role="option"
                  aria-selected={r === role}
                  data-index={i}
                >
                  <span class="picker-name">{r}</span>
                  <span class="picker-blurb">— {ROLE_BLURB[r]}</span>
                </li>
              {/each}
            </ul>
          {/if}
        </div>

        <label class="check">
          <input type="checkbox" bind:checked={mayShare} />
          <span>They may share it on</span>
        </label>
      </div>

      <p class="field-note">
        Anyone you add here gets it <strong>and any agents they have connected</strong>, at the
        same permission, straight away. Adding somebody who already has it changes what they can
        do.
      </p>

      {#if formError}<p class="sheet-error" role="alert">{formError}</p>{/if}

      <!-- The one live region in this form. See the note at the top. -->
      <p class="said" role="status">{news}</p>

      {#if attempts.length}
        <ul class="progress">
          {#each attempts as a (a.email)}
            <li class:failed={a.state === 'failed'}>
              <span class="progress-mark" aria-hidden="true">
                {a.state === 'done' ? '✓' : a.state === 'failed' ? '✕' : a.state === 'sending' ? '…' : '·'}
              </span>
              <span class="progress-email">{a.email}</span>
              {#if a.state === 'failed'}
                <span class="progress-why">{a.error}</span>
              {:else if a.state === 'done' && a.mailed === false}
                <!-- The grant is real; only the mail did not go out. Saying
                     nothing would leave a person expecting a notification that
                     is never coming. -->
                <span class="progress-why">shared, but the email could not be sent</span>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}

      <div class="sheet-actions">
        <button type="button" class="btn btn-quiet btn-small" onclick={onclose}>Close</button>
        <button type="submit" class="btn btn-primary btn-small" disabled={!canSend}>
          {sending ? 'Sharing…' : 'Share'}
        </button>
      </div>
    </form>

    {#if canPublish}
      <div class="publish">
        <h3 class="access-title">Anyone on the web</h3>
        <label class="publish-row">
          <input
            type="checkbox"
            checked={isPublic}
            disabled={publishing}
            onchange={(e) => togglePublic(e.currentTarget.checked)}
          />
          <span class="publish-label">
            <span class="publish-main">Publish this document to the web</span>
            <span class="field-note">
              {#if isPublic}
                Anyone with the link can read it — no account needed. They cannot comment or edit,
                and they do not see the comments. It is not listed in search engines.
              {:else}
                Off. Only the people above can open it.
              {/if}
            </span>
          </span>
        </label>
        {#if isPublic}
          <CopyBox label="Public link" text={publicUrl} hint="The same address it always had — signing in is not required to open it." />
        {/if}
        {#if publishError}<p class="sheet-error" role="alert">{publishError}</p>{/if}
      </div>
    {/if}

    <div class="access">
      <h3 class="access-title">Who has access</h3>
      {#if listError}<p class="sheet-error" role="alert">{listError}</p>{/if}
      {#if loading && entries.length === 0}
        <p class="field-note">Loading…</p>
      {:else if entries.length === 0}
        <p class="field-note">Nobody but you.</p>
      {:else}
        <ul class="access-list">
          {#each entries as entry (entry.userId ?? entry.via)}
            <li class="access-row">
              <span class="access-who">
                <span class="access-name">{entry.name}</span>
                {#if entry.email}<span class="access-email">{entry.email}</span>{/if}
                <span class="access-how">
                  {viaWords(entry)}{entry.mayShare && entry.via !== 'owner' ? ' · may share it on' : ''}
                  {#if entry.liveTokens}
                    · {agentWords(entry.liveTokens)}
                  {/if}
                  <!--
                    An account the share itself created. Worth saying, because
                    an owner glancing at this list would otherwise read a row
                    that looks exactly like a colleague's -- and the document is
                    sitting waiting rather than being read. `name` is the
                    address until they sign in, so the row is never blank.
                  -->
                  {#if entry.pending}
                    · <span class="access-pending">has not signed in yet</span>
                  {/if}
                </span>
              </span>
              <span class="access-role">{entry.role}</span>
              {#if entry.via === 'direct' && entry.userId}
                <button
                  type="button"
                  class="access-remove"
                  disabled={revoking === entry.userId}
                  onclick={() => revoke(entry)}
                >
                  Remove
                </button>
              {:else}
                <!-- The owner cannot be revoked (ownership is a column, not a
                     grant), and an org-wide or folder-derived grant is not
                     removed from one document's dialog. The space is held so
                     the rows line up. -->
                <span class="access-remove access-remove-empty" aria-hidden="true"></span>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </div>
</div>

<style>
  /* The backdrop and sheet are ModalSheet's, repeated rather than shared:
     ModalSheet is a confirm dialog -- one body, one confirm button -- and this
     is a form with a list under it that stays open while it works. Bending the
     shared component into taking both would have made every one of its four
     other callers carry the branch. */
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: grid;
    place-items: center;
    padding: 1rem;
    background: rgb(0 0 0 / 28%);
  }

  .backdrop-hit {
    position: absolute;
    inset: 0;
    border: 0;
    padding: 0;
    background: transparent;
    cursor: default;
  }

  .sheet {
    position: relative;
    width: min(34rem, 100%);
    max-height: 85vh;
    overflow-y: auto;
    padding: 1rem 1.1rem 1.1rem;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.65rem;
    background: var(--color-paper, #faf9f5);
    box-shadow: 0 18px 48px rgb(0 0 0 / 22%);
  }

  .sheet-title {
    margin: 0 0 0.75rem;
    font-size: 1rem;
  }

  .share-form {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .field-inline {
    flex: 1 1 14rem;
  }

  .field-label {
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }

  .field-input {
    width: 100%;
    font: inherit;
    padding: 0.4rem 0.55rem;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.375rem;
    background: var(--color-paper, #faf9f5);
    color: inherit;
  }

  /* ------------------------------------------------------ the address field */

  /* The box LOOKS like the input it replaced; the real input is inside it,
     borderless, and the pills sit above it and wrap as they need to. */
  .tokens {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 0.35rem 0.4rem;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.375rem;
    background: var(--color-paper, #faf9f5);
  }

  .tokens:focus-within {
    border-color: var(--color-ink-soft, #6e665c);
  }

  /* The input inside is borderless, so the focus ring belongs on the BOX --
     otherwise `outline: none` below takes the global :focus-visible ring away
     and leaves a keyboard user with no indicator at all. */
  .tokens:has(.token-input:focus-visible) {
    outline: 2px solid var(--color-red-deep, #c01030);
    outline-offset: 2px;
  }

  .tokens-bad,
  .tokens-bad:focus-within {
    border-color: var(--color-red-deep, #c01030);
  }

  .pills {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    max-width: 100%;
    padding: 0.1rem 0.15rem 0.1rem 0.45rem;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 999px;
    background: var(--color-plinth, #f1ece4);
    font-size: var(--text-meta, 0.8125rem);
  }

  .pill-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pill-x {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.15rem;
    height: 1.15rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--color-ink-soft, #6e665c);
    font: inherit;
    font-size: 0.95rem;
    line-height: 1;
    padding: 0;
    cursor: pointer;
  }

  .pill-x:hover,
  .pill-x:focus-visible {
    background: var(--color-paper, #faf9f5);
    color: var(--color-red-deep, #c01030);
  }

  .token-input {
    width: 100%;
    min-width: 8rem;
    border: 0;
    padding: 0.1rem 0.15rem;
    background: transparent;
    color: inherit;
    font: inherit;
  }

  .token-input:focus {
    outline: none;
  }

  .field-bad {
    margin: 0;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-red-deep, #c01030);
  }

  /* --------------------------------------------------- the permission list */

  .picker {
    display: flex;
    align-items: baseline;
    gap: 0.3rem;
    text-align: left;
    cursor: pointer;
  }

  .picker-name {
    flex: 0 0 auto;
  }

  .picker-blurb {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }

  .picker-caret {
    flex: 0 0 auto;
    font-size: 0.7rem;
    color: var(--color-ink-soft, #6e665c);
  }

  /* Fixed, so the sheet's own `overflow-y: auto` cannot clip it; the z-index
     resolves inside the backdrop's stacking context, above the sheet. */
  .picker-list {
    position: fixed;
    z-index: 60;
    list-style: none;
    margin: 0;
    padding: 0.25rem;
    max-height: 14rem;
    max-width: calc(100vw - 1rem);
    overflow-y: auto;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.375rem;
    background: var(--color-paper, #faf9f5);
    box-shadow: 0 12px 28px rgb(0 0 0 / 18%);
  }

  .picker-list:focus {
    outline: none;
  }

  .picker-option {
    display: flex;
    align-items: baseline;
    gap: 0.3rem;
    padding: 0.35rem 0.5rem;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .picker-option:hover,
  .picker-option.active {
    background: var(--color-plinth, #f1ece4);
  }

  .picker-option[aria-selected='true'] .picker-name {
    font-weight: 600;
  }

  .check {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: var(--text-meta, 0.8125rem);
    padding-top: 1.35rem;
  }

  .field-note {
    margin: 0;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }

  .sheet-error {
    margin: 0;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-red-deep, #c01030);
  }

  /* Announced, never drawn: the pills and the progress list are what a sighted
     person reads, and repeating them on screen would be noise. */
  .said {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .progress {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    font-size: var(--text-meta, 0.8125rem);
  }

  .progress li {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.4rem;
  }

  .progress li.failed {
    color: var(--color-red-deep, #c01030);
  }

  .progress-mark {
    width: 1rem;
    text-align: center;
  }

  .progress-why {
    color: var(--color-ink-soft, #6e665c);
  }

  .progress li.failed .progress-why {
    color: inherit;
  }

  .sheet-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .access {
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--color-line, #211d1824);
  }

  .publish {
    border-top: 1px solid var(--color-line);
    padding-top: 1.25rem;
    margin-top: 1.25rem;
  }

  .publish-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.625rem;
    align-items: start;
    cursor: pointer;
  }

  .publish-row input {
    margin-top: 0.2rem;
  }

  .publish-label {
    display: grid;
    gap: 0.25rem;
    min-width: 0;
  }

  .publish-main {
    font-weight: 500;
  }

  .access-title {
    margin: 0 0 0.5rem;
    font-size: var(--text-meta, 0.8125rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--color-ink-soft, #6e665c);
  }

  .access-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .access-pending {
    color: var(--color-ink-soft);
    font-style: italic;
  }

  .access-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0;
    border-bottom: 1px solid var(--color-line, #211d1824);
  }

  .access-row:last-child {
    border-bottom: 0;
  }

  .access-who {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .access-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .access-email,
  .access-how {
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .access-role {
    flex: 0 0 auto;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }

  .access-remove {
    flex: 0 0 auto;
    min-width: 5rem;
    border: 0;
    background: transparent;
    color: var(--color-red-deep, #c01030);
    font: inherit;
    font-size: var(--text-meta, 0.8125rem);
    padding: 0.2rem 0.4rem;
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .access-remove:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .access-remove-empty {
    display: inline-block;
  }
</style>
